import React from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {GetTheApp} from 'components/GetTheApp'
import {MyListHeaderEntry} from 'components/MyListHeaderEntry'
import {PreferencesWidgetWithStore} from 'components/PreferencesWidgetWithStore'
import {QuickFilters} from 'components/QuickFilters'
import {SearchBoxDesktop} from 'components/SearchBox/Search'
import {shouldDisplayStatelessNotificationPreferences} from 'components/User/NotificationPreferences/utils'
import UserWidget from 'components/UserWidget'
import {HeaderChatButton} from 'components/VioMcpChat/HeaderChatButton'
import {useInAppFirst} from 'hooks/useInAppFirst'
import {useShowPreferencesOverlay} from 'hooks/useShowPreferencesOverlay/useShowPreferencesOverlay'
import {getVclidCookie} from 'modules/cookies/selectors'
import {getAnalyticsPageName} from 'modules/meta/selectors'
import {getHomeUrlWithUTMParams} from 'utils/utmParams'

import {NavBar} from '@vio/atlas/NavBar'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useIsEmbeddedWithBrand} from '@vio/core/src/_web/brand/hooks/useIsEmbeddedWithBrand'
import {Page} from '@vio/core/src/analytics/types/Events'
import {
  getUser,
  getUserEmail,
  selectIsAuthenticated,
  selectIsLoading
} from '@vio/core/src/auth/modules/selectors'
import {Placement} from '@vio/core/src/deepLinking/types'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'
import {NavBarBrandLogo} from '@vio/shared/src/navBar'
import {MyListLinkComponent} from '@vio/shared/src/priceWatch/hooks/useTrackMyListLinkClick'
import {BrandLogo} from '@vio/shared/src/whiteLabel/BrandLogo/BrandLogo'
import {CustomHeader} from '@vio/shared/src/whiteLabel/CustomHeader'

const MY_LIST_PAGES = [Page.Home, Page.Search, Page.Accommodation]

// Below desktopMd (1280px) the NavBar wraps the center content onto its own
// full-width row (wrappedGrid), so the search box should fill it. At desktopMd
// and up the slot is inline again and the box hugs its content (unless the
// collapsing-header layout wants it to grow to fill the row).
const SearchBoxWrapper = styled.div<{isFullWidth?: boolean; wraps?: boolean}>`
  margin: 0 auto;
  max-width: 1440px;
  width: ${({isFullWidth, wraps}) =>
    isFullWidth || wraps ? '100%' : 'fit-content'};

  ${linariaMq.desktopMd} {
    width: ${({isFullWidth}) => (isFullWidth ? '100%' : 'fit-content')};
  }
`

const RightComponentWrapper = styled.div`
  display: flex;
  gap: ${cssTheme.layout.spacing.s300};
  align-items: center;
`

interface Props {
  /** Whether to show the search form in the header */
  hasSearchForm?: boolean
  /** Whether to show the filters bar in the header */
  hasFilters?: boolean
  /** Custom component to override default search box */
  customSearchBox?: React.ReactElement
  /** Whether to show the date confirmation tooltip */
  showDateConfirmationTooltip?: boolean
  /** Whether to hide user widget in header*/
  disableUserWidget?: boolean
  /** Whether to hide the "Get the app" button in header */
  disableGetTheApp?: boolean
}

export const DesktopHeaderContent = ({
  hasSearchForm,
  hasFilters = false,
  customSearchBox,
  showDateConfirmationTooltip = false,
  disableUserWidget = false,
  disableGetTheApp = false
}: Props) => {
  const {brand, brandCode} = useBrand()
  const {shell, auth} = useBrandCapabilities()
  const isEmbedded = useIsEmbeddedWithBrand()
  const {isInAppFirst} = useInAppFirst()

  const analyticsPageName = useSelector(getAnalyticsPageName)
  const vioClickId = useSelector(getVclidCookie)

  const isHydrated = useIsHydrated()
  const logoURL = isHydrated ? getHomeUrlWithUTMParams() : '/'
  // SRP desktop header = 3 shapes from 2 brand flags (custom ⟹ collapsing):
  //   Vio default  !isCollapsing              static bar: logo · search · widgets
  //   WL standard  isCollapsing && !isCustom  same chrome, collapses on scroll,
  //                                           full-width search             [dealbase]
  //   WL custom    isCustom                   <CustomHeader> on top; NavBar
  //                                           carries only search + filters [kiwi, hp, finn]
  const isCustom = shell.hasCustomHeader && !isInAppFirst
  const isCollapsing = shell.hasCollapsingHeader
  // Custom brands render their own logo/nav, so we drop our chrome row for them.
  const showChrome = !isCustom
  // Collapsing brands only need the bar once there's a search form to host; the
  // standalone collapsing top bar isn't rendered when embedded.
  const showNavBar =
    !isCollapsing || hasSearchForm || (showChrome && !isEmbedded)

  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isLoading = useSelector(selectIsLoading)
  const showGetTheAppButton =
    !disableGetTheApp &&
    auth.showDesktopSignInCta &&
    !hasSearchForm &&
    !isAuthenticated &&
    !isLoading

  const user = useSelector(getUser)
  const userEmail = getUserEmail(user)
  const shouldDisplayStateless = shouldDisplayStatelessNotificationPreferences(
    isAuthenticated,
    analyticsPageName,
    userEmail
  )

  const handleShowPreferences = useShowPreferencesOverlay()

  const hasUserWidget = !disableUserWidget && auth.showUserWidget

  const showWidgets = showChrome && !shouldDisplayStateless

  const logo = (
    <NavBarBrandLogo
      brand={brand}
      logoUrl={logoURL}
      BrandLogoComponent={BrandLogo}
    />
  )

  // The search box always lives in the centerContent and drops to its own
  // full-width row on narrow desktop via wrapCenterContent. It never relocates
  // to another slot or component.
  const startContent = showChrome ? logo : undefined

  const searchBox = hasSearchForm
    ? customSearchBox || (
        <SearchBoxDesktop
          showDateConfirmationTooltip={showDateConfirmationTooltip}
        />
      )
    : undefined

  // The center content drops to its own full-width row below desktopMd; the
  // search box wrapper mirrors this so it fills that row while it is wrapped.
  const centerContentWraps = showChrome && Boolean(searchBox)

  const endContent = showWidgets ? (
    <RightComponentWrapper>
      {showGetTheAppButton && (
        <GetTheApp
          dataId="desktop_top_bar_home_page"
          placement={Placement.MenuItem}
        />
      )}
      <HeaderChatButton />
      <MyListHeaderEntry
        component={MyListLinkComponent.DesktopHeader}
        isEligible={MY_LIST_PAGES.includes(analyticsPageName)}
      />
      <PreferencesWidgetWithStore />
      {hasUserWidget && <UserWidget />}
    </RightComponentWrapper>
  ) : undefined

  return (
    <>
      {isCustom && (
        <CustomHeader
          brandCode={brandCode}
          isFixed={false}
          isFloating={true}
          currencySelector={<PreferencesWidgetWithStore />}
          onShowPreferences={handleShowPreferences}
          vioClickId={vioClickId}
          isHotelsActive={Boolean(hasSearchForm)}
        />
      )}
      {showNavBar && (
        <NavBar
          withDivider
          wrapCenterContent={centerContentWraps}
          startContent={startContent}
          centerContent={
            searchBox && (
              <SearchBoxWrapper
                isFullWidth={isCollapsing}
                wraps={centerContentWraps}
              >
                {searchBox}
              </SearchBoxWrapper>
            )
          }
          endContent={endContent}
          bottomContent={hasFilters ? <QuickFilters /> : undefined}
        />
      )}
    </>
  )
}
